"use client";
import {
	FC,
	useEffect,
	useState,
	useRef,
	useMemo,
	memo,
	createContext,
	useContext,
} from "react";
import Grid from "elements/Grid";
import ResourceSlider from "../shared/ResourceSlider";
import { CMS, ContentfulEntryType } from "utils/types";
import { useRouter } from "next/navigation";
import ButtonV2 from "elements/ButtonV2";
import RenderResourceCard from "./shared/RenderResourceCard";

import CarouselCaretLeft from "public/resources/left-arrow.svg";
import CarouselCaretRight from "public/resources/right-arrow.svg";
import HoverCarouselCaretLeft from "public/resources/hover-left-arrow.svg";
import HoverCarouselCaretRight from "public/resources/hover-right-arrow.svg";

import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation, Autoplay } from "swiper";
import QuickLink from "elements/QuickLink";
import { useResponsiveContext } from "context/ResponsiveContext";

export const sortByDateDesc = (a: CMS.CMSResource, b: CMS.CMSResource) =>
	new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

// Reusable Carousel Navigation Buttons Component
export const CarouselNavigationButtons: FC<{
	prevId?: string;
	nextId?: string;
	className?: string;
}> = ({
	prevId = "featured-resource-carousel-nav-prev",
	nextId = "featured-resource-carousel-nav-next",
	className = "nav-buttons-container mt-3",
}) => {
	const [isLeftArrowHovered, setIsLeftArrowHovered] = useState<boolean>(false);
	const [isRightArrowHovered, setIsRightArrowHovered] =
		useState<boolean>(false);

	return (
		<div className={className}>
			<div id={prevId}>
				<button
					onMouseEnter={() => setIsLeftArrowHovered(true)}
					onMouseLeave={() => setIsLeftArrowHovered(false)}>
					{isLeftArrowHovered ? (
						<HoverCarouselCaretLeft />
					) : (
						<CarouselCaretLeft />
					)}
				</button>
			</div>
			<div id={nextId}>
				<button
					onMouseEnter={() => setIsRightArrowHovered(true)}
					onMouseLeave={() => setIsRightArrowHovered(false)}>
					{isRightArrowHovered ? (
						<HoverCarouselCaretRight />
					) : (
						<CarouselCaretRight />
					)}
				</button>
			</div>
		</div>
	);
};

const HeaderImageContext = createContext<{
	url: string;
	setHeaderHeroImgUrl: (url: string) => void;
}>({
	url: "",
	setHeaderHeroImgUrl: () => {},
});

export const HeaderImageProvider: FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [headerHeroImgUrl, setHeaderHeroImgUrl] = useState("");

	return (
		<HeaderImageContext.Provider
			value={{
				url: headerHeroImgUrl,
				setHeaderHeroImgUrl,
			}}>
			{children}
			<HeaderImage />
		</HeaderImageContext.Provider>
	);
};

const HeaderImage = memo(() => {
	const { url } = useContext(HeaderImageContext);
	return (
		<div
			className="header-section-image-wrapper"
			style={{ backgroundImage: `url(${url})` }}>
			<div className="header-section-image-overlay" />
		</div>
	);
});

HeaderImage.displayName = "HeaderImage";

const FeaturedResourceCarouselSlides = ({
	resources,
}: {
	resources: CMS.CMSResource[];
}) => {
	const [render, setRender] = useState(false);
	const { setHeaderHeroImgUrl } = useContext(HeaderImageContext);

	const swiperRef = useRef<any>(null);
	const { isMobile } = useResponsiveContext();

	useEffect(() => {
		// No SSR on carousel
		setRender(true);
	}, []);

	const BlogPostSlidesArr = useMemo(
		() =>
			resources.map((props, i) => (
				<SwiperSlide
					key={props.slug}
					style={{ width: isMobile ? "300px" : "500px" }}>
					<FeaturedResourceSlide resource={props} />
				</SwiperSlide>
			)),
		[resources, isMobile]
	);

	if (!render) return null;

	const pauseAutoplayTemporarily = () => {
		swiperRef.current?.autoplay?.stop();
		setTimeout(() => {
			swiperRef.current?.autoplay?.start();
		}, 5000);
	};

	return (
		<div
			className="featured-blog-post-carousel-container"
			onMouseEnter={() => swiperRef.current?.autoplay?.stop()}
			onMouseLeave={() => swiperRef.current?.autoplay?.start()}
			onClick={pauseAutoplayTemporarily}>
			<Swiper
				spaceBetween={20}
				observer
				resizeObserver
				observeParents
				loop={true}
				slidesPerView="auto"
				modules={[Pagination, Navigation, Autoplay]}
				navigation={{
					nextEl: "#featured-resource-carousel-nav-next",
					prevEl: "#featured-resource-carousel-nav-prev",
				}}
				onSlideChange={swiper => {
					const activeIndex = swiper.realIndex;
					const activeSlide = resources[activeIndex];
					setHeaderHeroImgUrl(activeSlide.heroImage.fields.file.url);
				}}
				onSwiper={swiper => {
					swiperRef.current = swiper;
					const activeIndex = swiper.realIndex;
					const activeSlide = resources[activeIndex];
					setHeaderHeroImgUrl(activeSlide.heroImage.fields.file.url);
				}}
				autoplay={{ delay: 5000, disableOnInteraction: false }}>
				{BlogPostSlidesArr}
			</Swiper>
		</div>
	);
};

const FeaturedResourceSlide = ({ resource }: { resource: CMS.CMSResource }) => {
	return (
		<div className="featured-blog-post-carousel-slide">
			<RenderResourceCard
				key={resource.slug}
				resource={resource}
				index={0}
				mobileLoadMore={false}
				variant="featured"
			/>
		</div>
	);
};

export const FeaturedResourceCarousel = ({
	resources,
}: {
	resources: CMS.CMSResource[];
}) => {
	return (
		<div className="featured-blog-post-carousel-wrapper">
			<FeaturedResourceCarouselSlides resources={resources} />
		</div>
	);
};

export const RecentResourceSection = ({
	resources,
	mobileLoadMore,
}: {
	resources: CMS.CMSResource[];
	mobileLoadMore: boolean;
}) => {
	return (
		<div className="blog-post-card-container-full-width">
			<div className="blog-post-card-container-half-width">
				{resources
					.sort(sortByDateDesc)
					.slice(0, 1)
					.map((resource, index) => (
						<RenderResourceCard
							key={resource.slug + index}
							resource={resource}
							index={index}
							mobileLoadMore={mobileLoadMore}
						/>
					))}
			</div>
			<div className="blog-post-card-container-half-width flex-column">
				{resources
					.sort(sortByDateDesc)
					.slice(1, 3)
					.map((resource, index) => (
						<RenderResourceCard
							key={resource.slug + index}
							resource={resource}
							index={index}
							mobileLoadMore={mobileLoadMore}
							variant="horizontal"
						/>
					))}
			</div>
		</div>
	);
};

const ResourcesPage: FC<{
	caseStudies: (
		| CMS.CommercialCaseStudyFields
		| CMS.ResidentialCaseStudyFields
	)[];
	blogPosts: CMS.BlogPostFields[];
	insights: (CMS.InsightFields | CMS.GatedInsightFields)[];
}> = ({ caseStudies, blogPosts, insights }) => {
	const [resources, setResources] = useState(
		blogPosts.sort(sortByDateDesc).slice(3)
	);
	const [mobileLoadMore, setMobileLoadMore] = useState(false);
	const [filter, setFilter] = useState<string>();
	const [clearFilter, setClearFilter] = useState(false);

	const router = useRouter();

	const featuredBlogPosts = useMemo(
		() => blogPosts.filter(post => post.featured === true).sort(sortByDateDesc),
		[blogPosts]
	);

	const handleFilterClick = (word: string) => {
		if (word === "all") {
			setFilter(undefined);
			setClearFilter(false);
		} else {
			setFilter(word);
			setClearFilter(true);
		}
	};

	useEffect(() => {
		if (filter) {
			setResources(() =>
				blogPosts
					.sort(sortByDateDesc)
					.slice(3)
					.filter(resource => {
						return resource.tags.some(
							tag => tag.toLowerCase() === filter.toLowerCase()
						);
					})
			);
		} else {
			setResources(blogPosts.sort(sortByDateDesc).slice(3));
		}
	}, [filter, blogPosts]);

	return (
		<div id="resources-page">
			<div className="header-section-wrapper">
				<HeaderImageProvider>
					<div className="header-section">
						<div className="left-section">
							<h1 className="h1 white">Blog</h1>
							<p className="white mt-2">
								Our blogs provide the most useful information to help property
								owners manage their real estate with confidence.
							</p>
							<CarouselNavigationButtons />
						</div>
						<div className="right-section">
							<FeaturedResourceCarousel resources={featuredBlogPosts} />
						</div>
					</div>
				</HeaderImageProvider>
			</div>
			<Grid>
				<div className="other-resources-section mt-4 mb-4">
					<h4 className="h4 denim mb-3">Want Additional Learnings?</h4>
					<div className="other-resources-section-container">
						<div className="other-resources-section-container-item">
							<QuickLink
								className="white"
								size="extra-large"
								semibold
								href="/case-study">
								Case Studies
							</QuickLink>
							<p className="small white">
								We explore meaningful and high impact results using real world
								examples and market research.
							</p>
						</div>
						<div className="other-resources-section-container-item">
							<QuickLink
								className="white"
								size="extra-large"
								semibold
								href="/insight">
								Insights
							</QuickLink>
							<p className="small white">
								We dive deep into complex topics, markets and trends to bring
								you unparalleled insights.
							</p>
						</div>
					</div>
				</div>
			</Grid>
			<div className="grey-gradient-background-bottom">
				<div id="blog-posts">
					<Grid>
						<div className="resources-common-grid-divider">
							<h3 className="mb-4">Most Recent Blog Posts</h3>
							<div className="most-recent-resources-container column-arrangement">
								<RecentResourceSection
									resources={blogPosts}
									mobileLoadMore={mobileLoadMore}
								/>
							</div>
						</div>
					</Grid>
				</div>
			</div>
			<div className="filter-tabs mb-4">
				<Grid>
					<div className="filter-tabs-container">
						<p
							className={
								"filter-tab sm text-center denim_5 " +
								(!filter ? "active bold" : "")
							}
							onClick={() => handleFilterClick("all")}>
							All
						</p>
						<p
							className={
								"filter-tab sm text-center denim_5 " +
								(filter === "residential" ? "active bold" : "")
							}
							onClick={() => handleFilterClick("residential")}>
							Residential
						</p>
						<p
							className={
								"filter-tab sm text-center denim_5 " +
								(filter === "commercial" ? "active bold" : "")
							}
							onClick={() => handleFilterClick("commercial")}>
							Commercial
						</p>
					</div>
				</Grid>
			</div>
			<div id="resources-page-all">
				<Grid>
					<h2 className="display2 resources-page-all-title mb-2">
						All Blog Posts
					</h2>
				</Grid>
				<div className="resources-more-cards-section">
					<Grid id="resources-more-cards-section-grid">
						{resources.length ? (
							resources
								.sort(sortByDateDesc)
								.map((resource, index) => (
									<RenderResourceCard
										key={resource.slug + index}
										resource={resource}
										index={index}
										mobileLoadMore={mobileLoadMore}
									/>
								))
						) : (
							<h4 className="resources-more-cards-section-no-filter-prompt">
								Oops! No resources found, try filtering by something else
							</h4>
						)}
						<div
							className={
								mobileLoadMore
									? "resources-more-cards-section-load-more-btn-hide"
									: "resources-more-cards-section-load-more-btn"
							}>
							<ButtonV2
								size="small"
								onClick={() => {
									setMobileLoadMore(true);
								}}>
								Load More
							</ButtonV2>
						</div>
					</Grid>
				</div>
			</div>
			<div className="grey-gradient-background">
				<div id="recent-case-studies">
					<Grid>
						<div className="resources-common-grid-divider mt-4">
							<h3>Recent Case Studies</h3>
							<ResourceSlider
								swiperID="recent-case-studies"
								resourceData={caseStudies.sort(sortByDateDesc).slice(0, 3)}
								type="casestudy"
							/>
							<ButtonV2
								className="mt-6 mx-auto"
								variant="primary"
								size="default"
								onClick={() => {
									router.push("/case-study");
								}}>
								View More Case Studies
							</ButtonV2>
						</div>
					</Grid>
				</div>
			</div>
			<div className="grey-gradient-background">
				<div id="recent-insights">
					<Grid>
						<div className="resources-common-grid-divider mt-4">
							<h3>Recent Insights</h3>
							<ResourceSlider
								swiperID="recent-insights"
								resourceData={insights.sort(sortByDateDesc).slice(0, 3)}
								type="insight"
							/>
							<ButtonV2
								className="mt-6 mx-auto"
								variant="primary"
								size="default"
								onClick={() => {
									router.push("/insight");
								}}>
								View More Insights
							</ButtonV2>
						</div>
					</Grid>
				</div>
			</div>
		</div>
	);
};

export default ResourcesPage;
